import { DestroyRef, Injectable, effect, inject, signal, untracked } from '@angular/core';

import { LoggerService } from '@gde-mit/angular-core';

import type {
  CoverCandidate,
  CoverGenerationQueuePort,
  CoverResult,
  CoverRunOutcome,
} from '@shared/models/cover-generation-queue.port';

import { UserService } from '@shared/services/user.service';

import { httpErrorStatus } from '@shared/utils/http-error-status.util';
import { httpErrorTitle } from '@shared/utils/http-error-title.util';

import { CoverGenerationFailure, CoverGenerationStatus } from '@api';

import { StatusCodes } from 'http-status-codes';

import { AppSettingsService } from '../../../../../app-settings.service';
import { ActivityService } from './activity.service';
import { isCoverAutoEligible } from './cover-auto-eligibility';

const AUTOMATIC_CALL_LIMIT = 2;

// Longer than the cover circuit breaker's break duration, so the pause outlasts the open breaker.
const UNAVAILABLE_PAUSE_MS = 2 * 60 * 1000;

const PLACEHOLDER_CONTENT_TITLE = 'ActivityCover.PlaceholderContent';
const PIPELINE_DISABLED_TITLE = 'ImageUpload.Disabled';

// Only a 502 or 504 carrying one of these codes in its ProblemDetails comes from Kt.Api; a load balancer's has none.
const FAILURE_BY_COVER_ERROR_CODE: ReadonlyMap<string, CoverGenerationFailure> = new Map([
  ['CoverGeneration.ImageRejected', CoverGenerationFailure.IMAGE_REJECTED],
  ['CoverGeneration.Rejected', CoverGenerationFailure.REJECTED],
  ['CoverGeneration.Timeout', CoverGenerationFailure.TIMED_OUT],
  ['CoverGeneration.Unavailable', CoverGenerationFailure.UNAVAILABLE],
]);

interface CoverRun {
  readonly activityId: string;
  readonly signal: AbortSignal;
  readonly seed: CoverResult | undefined;
  readonly epoch: number;
  readonly baseline: CoverResult | undefined;
  readonly isManual: boolean;
}

interface PendingCall {
  readonly activityId: string;
  readonly seed: CoverResult;
  readonly candidate: CoverCandidate;
}

/**
 * The client's single owner of live cover state: every cover generation call goes through it, and every card, header
 * and dialog reads the activity's cover from it.
 *
 * Provided in root and reached from outside the product shell through the `COVER_GENERATION_QUEUE` port. A landed
 * load or a call's answer becomes the activity's result; {@link coverOf} answers that result in place of the host's own
 * load, so a surface seeded from an older load never shows Generate during a run. Everything is emptied when another
 * user signs in.
 */
@Injectable({ providedIn: 'root' })
export class CoverGenerationQueue implements CoverGenerationQueuePort {
  readonly #activityService = inject(ActivityService);
  readonly #userService = inject(UserService);
  readonly #logger = inject(LoggerService);
  readonly #appSettings = inject(AppSettingsService);

  readonly #results = signal<ReadonlyMap<string, CoverResult>>(new Map());
  readonly #inFlightCounts = signal<ReadonlyMap<string, number>>(new Map());
  readonly #knownIds = signal<ReadonlySet<string>>(new Set());
  readonly #paused = signal(false);
  readonly #disabled = signal(false);

  /** True while a 502 `CoverGeneration.Unavailable` pause is running; automatic calls are refused until it ends. */
  readonly paused = this.#paused.asReadonly();
  /** True once the server refused with `ImageUpload.Disabled`, until a reload or another user. */
  readonly disabled = this.#disabled.asReadonly();
  #signedInUserId = this.#currentUserId();
  #pauseTimer: ReturnType<typeof setTimeout> | undefined;
  #pendingCalls: PendingCall[] = [];
  #runningAutomaticCalls = 0;
  /** Bumped when another user signs in; a call started under an earlier epoch lands nothing. */
  #epoch = 0;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      clearTimeout(this.#pauseTimer);
    });

    effect(() => {
      const userId = this.#currentUserId();

      untracked(() => {
        this.#resetWhenUserChanged(userId);
      });
    });
  }

  /**
   * Queues a generation for the candidate when it passes {@link isCoverAutoEligible}, judged against what the queue
   * already holds for the id; anything else is ignored. At most two calls run at a time, in the order they arrived.
   *
   * @param candidate The activity as a load or a create trigger knows it.
   */
  enqueue(candidate: CoverCandidate): void {
    this.#syncSignedInUser();

    const { activityId } = candidate;
    const seed = untracked(() => this.#seedFor(activityId, candidate));
    const isKnown = untracked(() => this.known(activityId));

    if (!this.#isAutoEligible(candidate, seed, isKnown)) {
      return;
    }

    this.#markStarted(activityId);
    this.#pendingCalls.push({ activityId, seed, candidate });
    this.#startPendingCalls();
  }

  inFlight(activityId: string): boolean {
    return (this.#inFlightCounts().get(activityId) ?? 0) > 0;
  }

  coverOf(activityId: string, seed: CoverResult): CoverResult {
    if (this.#currentUserId() !== this.#signedInUserId) {
      return seed;
    }

    return this.#results().get(activityId) ?? seed;
  }

  /**
   * Keeps what a landed load says about an activity's cover, unless a started call for it is still running, or the
   * load answers from before the generation record the queue already holds. A load is kept for a merely queued call,
   * whose started cover the automatic re-check then reads before it runs.
   *
   * @param activityId The activity the load is about.
   * @param loaded A list item, a detail or a bare result. Only its cover fields are stored, so the stored result
   *   stays equal while the rest of the item changes.
   * @param loaded.cover The current cover the load answered, or null.
   * @param loaded.coverGeneration The generation record the load answered, or null.
   */
  remember(activityId: string, { cover, coverGeneration }: CoverResult): void {
    this.#syncSignedInUser();

    if (untracked(() => this.#hasRunningCall(activityId))) {
      return;
    }

    const result: CoverResult = { cover, coverGeneration };
    const stored = untracked(() => this.#results().get(activityId));

    if (isSameResult(stored, result) || isOlderGeneration(result, stored)) {
      return;
    }

    this.#setResult(activityId, result);
  }

  /**
   * Whether the queue holds the activity queued or has started a call for it this session, until a pause or the
   * disabled latch drops the call before it starts. The automatic rules never pick a known activity.
   *
   * @param activityId The activity to look up.
   * @returns Whether the activity is known.
   */
  known(activityId: string): boolean {
    return this.#knownIds().has(activityId);
  }

  /**
   * Generates a cover at once, outside any cap, and records what the server answered as the activity's result.
   *
   * @param activityId The activity to generate for.
   * @param signal Aborting cancels the request in flight.
   * @param seed What the calling surface shows now; a failure keeps its cover when the queue holds no result yet.
   * @returns How the call ended. `rereadFromServer` and `aborted` mean the result is what a fresh read of the
   * activity returned, or unchanged when that read failed too or when a newer result landed for the activity while
   * this call ran (see `#isUnchangedSince`); a recorded failure (`failed`) or a 409 (`inProgress`) likewise never
   * overwrites such a newer result. A 401 or a user change also ends as `aborted` with nothing recorded: the app's
   * auth handling takes over, or the call belongs to the earlier user.
   */
  async runNow(activityId: string, signal: AbortSignal, seed?: CoverResult): Promise<CoverRunOutcome> {
    this.#syncSignedInUser();

    const run: CoverRun = {
      activityId,
      signal,
      seed,
      epoch: this.#epoch,
      baseline: untracked(() => this.#results().get(activityId)),
      isManual: true,
    };
    this.#withdrawPendingCall(activityId);
    this.#markStarted(activityId);

    try {
      return await this.#generate(run);
    } finally {
      if (run.epoch === this.#epoch) {
        this.#releaseCall(activityId);
      }
    }
  }

  #seedFor(activityId: string, seed: CoverResult): CoverResult {
    const { cover, coverGeneration } = this.coverOf(activityId, seed);

    return { cover, coverGeneration };
  }

  #isAutoEligible(candidate: CoverCandidate, current: CoverResult, isKnown: boolean): boolean {
    return untracked(() =>
      isCoverAutoEligible(
        { ...candidate, cover: current.cover, coverGeneration: current.coverGeneration },
        {
          isEnabled: this.#appSettings.coverGenerationEnabled,
          isKnown,
          isPaused: this.#paused(),
          isDisabled: this.#disabled(),
        },
      ),
    );
  }

  #startPendingCalls(): void {
    while (this.#runningAutomaticCalls < AUTOMATIC_CALL_LIMIT) {
      const call = this.#pendingCalls.shift();

      if (call === undefined) {
        return;
      }

      this.#runningAutomaticCalls++;
      void this.#runAutomatic(call);
    }
  }

  async #runAutomatic({ activityId, seed, candidate }: PendingCall): Promise<void> {
    const current = untracked(() => this.#seedFor(activityId, seed));

    if (!this.#isAutoEligible(candidate, current, false)) {
      this.#runningAutomaticCalls--;
      this.#releaseCall(activityId);
      this.#startPendingCalls();

      return;
    }

    const run: CoverRun = {
      activityId,
      signal: new AbortController().signal,
      seed,
      epoch: this.#epoch,
      baseline: untracked(() => this.#results().get(activityId)),
      isManual: false,
    };

    try {
      await this.#generate(run);
    } catch (error) {
      this.#logger.warn(`Cover generation: the automatic call for ${activityId} ended unexpectedly`, error);
    } finally {
      this.#runningAutomaticCalls--;

      if (run.epoch === this.#epoch) {
        this.#releaseCall(activityId);
      }

      this.#startPendingCalls();
    }
  }

  async #generate(run: CoverRun): Promise<CoverRunOutcome> {
    let generated: CoverResult;

    try {
      const { cover, coverGeneration } = await this.#activityService.generateCover(run.activityId, {
        signal: run.signal,
      });
      generated = { cover, coverGeneration };
    } catch (error) {
      return this.#settleFailure(run, error);
    }

    if (run.epoch !== this.#epoch) {
      return 'aborted';
    }

    this.#setResult(run.activityId, generated);

    if (run.isManual) {
      this.#resumeAutomaticCalls();
    }

    return 'succeeded';
  }

  async #settleFailure(run: CoverRun, error: unknown): Promise<CoverRunOutcome> {
    if (run.epoch !== this.#epoch) {
      return 'aborted';
    }

    if (run.signal.aborted) {
      await this.#rereadFromServer(run);

      return 'aborted';
    }

    const status = httpErrorStatus(error);
    const title = httpErrorTitle(error);
    const failure = recordedFailure(status, title);

    if (failure !== undefined) {
      this.#setFailed(run, failure);

      if (failure === CoverGenerationFailure.UNAVAILABLE && status === StatusCodes.BAD_GATEWAY) {
        this.#pauseAutomaticCalls();
      }

      return 'failed';
    }

    if (isServerUnreachable(status)) {
      await this.#rereadFromServer(run);

      return 'rereadFromServer';
    }

    return this.#settleRefusal(run, status, title);
  }

  #settleRefusal(run: CoverRun, status: number | undefined, title: string | undefined): CoverRunOutcome {
    switch (status) {
      case StatusCodes.UNAUTHORIZED: {
        return 'aborted';
      }
      case StatusCodes.NOT_FOUND: {
        return 'notFound';
      }
      case StatusCodes.CONFLICT: {
        this.#setRequested(run);

        return 'inProgress';
      }
      case StatusCodes.BAD_REQUEST: {
        return this.#settleBadRequest(title);
      }
      default: {
        return 'failed';
      }
    }
  }

  #settleBadRequest(title: string | undefined): CoverRunOutcome {
    if (title === PLACEHOLDER_CONTENT_TITLE) {
      return 'placeholder';
    }

    if (title === PIPELINE_DISABLED_TITLE) {
      this.#disabled.set(true);
      this.#dropPendingCalls();

      return 'disabled';
    }

    return 'failed';
  }

  async #rereadFromServer(run: CoverRun): Promise<void> {
    try {
      const { cover, coverGeneration } = await this.#activityService.getActivityInstanceById(run.activityId);

      if (run.epoch === this.#epoch && this.#isUnchangedSince(run)) {
        this.#setResult(run.activityId, { cover, coverGeneration });
      }
    } catch (error) {
      this.#logger.warn(
        `Cover generation: re-reading ${run.activityId} failed; the next load corrects the state`,
        error,
      );
    }
  }

  #setFailed(run: CoverRun, failure: CoverGenerationFailure): void {
    if (!this.#isUnchangedSince(run)) {
      return;
    }

    this.#setResult(run.activityId, {
      cover: this.#earlierCover(run),
      coverGeneration: { status: CoverGenerationStatus.FAILED, failure, requestedAt: null },
    });
  }

  #setRequested(run: CoverRun): void {
    if (!this.#isUnchangedSince(run)) {
      return;
    }

    this.#setResult(run.activityId, {
      cover: this.#earlierCover(run),
      coverGeneration: { status: CoverGenerationStatus.REQUESTED, failure: null, requestedAt: null },
    });
  }

  #earlierCover(run: CoverRun): CoverResult['cover'] {
    return untracked(() => this.#results().get(run.activityId))?.cover ?? run.seed?.cover ?? null;
  }

  // A later call for the same activity may have landed a newer result, which an earlier call's answer must not overwrite.
  #isUnchangedSince(run: CoverRun): boolean {
    return untracked(() => this.#results().get(run.activityId)) === run.baseline;
  }

  #setResult(activityId: string, result: CoverResult): void {
    this.#results.update((results) => new Map(results).set(activityId, result));
  }

  #markStarted(activityId: string): void {
    this.#knownIds.update((ids) => new Set(ids).add(activityId));
    this.#inFlightCounts.update((counts) => new Map(counts).set(activityId, (counts.get(activityId) ?? 0) + 1));
  }

  #releaseCall(activityId: string): void {
    this.#inFlightCounts.update((counts) => {
      const remaining = new Map(counts);
      const running = (remaining.get(activityId) ?? 1) - 1;

      if (running > 0) {
        remaining.set(activityId, running);
      } else {
        remaining.delete(activityId);
      }

      return remaining;
    });
  }

  #hasRunningCall(activityId: string): boolean {
    return this.inFlight(activityId) && !this.#pendingCalls.some((call) => call.activityId === activityId);
  }

  // The manual call is the one that runs; the queued call for the same id would only earn a 409.
  #withdrawPendingCall(activityId: string): void {
    const index = this.#pendingCalls.findIndex((call) => call.activityId === activityId);

    if (index !== -1) {
      this.#pendingCalls.splice(index, 1);
      this.#releaseCall(activityId);
    }
  }

  // A dropped call holds no record on the server and is no longer known: after a pause the lazy rule can pick its
  // activity up on a later load, while the disabled latch keeps every automatic call out for the session.
  #dropPendingCalls(): void {
    const dropped = this.#pendingCalls;
    this.#pendingCalls = [];

    for (const { activityId } of dropped) {
      this.#releaseCall(activityId);
      this.#knownIds.update((ids) => {
        const remaining = new Set(ids);
        remaining.delete(activityId);

        return remaining;
      });
    }
  }

  #pauseAutomaticCalls(): void {
    clearTimeout(this.#pauseTimer);
    this.#dropPendingCalls();
    this.#paused.set(true);
    this.#pauseTimer = setTimeout(() => {
      this.#paused.set(false);
    }, UNAVAILABLE_PAUSE_MS);
  }

  #resumeAutomaticCalls(): void {
    clearTimeout(this.#pauseTimer);
    this.#paused.set(false);
  }

  #currentUserId(): string | null {
    return this.#userService.userData()?.id ?? null;
  }

  /** The reset effect runs after a user change; a call or a load arriving before it must already see the change. */
  #syncSignedInUser(): void {
    this.#resetWhenUserChanged(untracked(() => this.#currentUserId()));
  }

  #resetWhenUserChanged(userId: string | null): void {
    if (userId === this.#signedInUserId) {
      return;
    }

    this.#signedInUserId = userId;
    this.#epoch++;
    this.#pendingCalls = [];
    this.#results.set(new Map());
    this.#inFlightCounts.set(new Map());
    this.#knownIds.set(new Set());
    this.#disabled.set(false);
    this.#resumeAutomaticCalls();
  }
}

/**
 * Places a generation record on the server's timeline.
 *
 * @param generation The record of a result, if it has one.
 * @returns The attempt's start time (`-Infinity` when unknown) and a rank for how far the attempt has come:
 *   -1 for no attempt, 0 while running, 1 once ended, 2 once a teacher's upload superseded it. The supersede is
 *   the newest state of an attempt, so for the same attempt it outranks `Failed` and `Succeeded`.
 */
function generationOrder(generation: CoverResult['coverGeneration']): readonly [number, number] {
  if (!generation) {
    return [Number.NEGATIVE_INFINITY, -1];
  }

  const start = generation.requestedAt ? Date.parse(generation.requestedAt) : Number.NEGATIVE_INFINITY;

  switch (generation.status) {
    case CoverGenerationStatus.SKIPPED: {
      return [start, -1];
    }
    case CoverGenerationStatus.REQUESTED: {
      return [start, 0];
    }
    case CoverGenerationStatus.SUPERSEDED: {
      return [start, 2];
    }
    default: {
      return [start, 1];
    }
  }
}

/**
 * Whether an incoming result describes the activity's generation record from before the stored one. The server's
 * records only move forward: a later attempt starts later, and one attempt goes from `Requested` to its end. A
 * record without a start time (`Skipped`, none, or one the client wrote itself) ranks below any attempt, and equal
 * records are never older, so a saved framing or an identical reload still lands.
 *
 * @param incoming The result about to be stored.
 * @param stored The result the queue holds, if any.
 * @returns Whether storing `incoming` would move the activity back in time.
 */
function isOlderGeneration(incoming: CoverResult, stored: CoverResult | undefined): boolean {
  if (stored === undefined) {
    return false;
  }

  const [incomingStart, incomingRank] = generationOrder(incoming.coverGeneration);
  const [storedStart, storedRank] = generationOrder(stored.coverGeneration);

  return incomingStart < storedStart || (incomingStart === storedStart && incomingRank < storedRank);
}

/**
 * Whether a load already stored for the activity carries exactly the incoming result.
 *
 * @param stored The result the queue holds, if any.
 * @param incoming The result about to be stored.
 * @returns Whether storing `incoming` would change nothing.
 */
function isSameResult(stored: CoverResult | undefined, incoming: CoverResult): boolean {
  return stored !== undefined && JSON.stringify(stored) === JSON.stringify(incoming);
}

/**
 * Tells a lost connection, or a 502 or 504 without a cover error code from the load balancer, from an answer of
 * `Kt.Api`: the server may or may not have recorded the run.
 *
 * @param status The HTTP status of the failed call, if it had one.
 * @returns Whether the call ended without an answer from `Kt.Api`.
 */
function isServerUnreachable(status: number | undefined): boolean {
  return status === 0 || status === StatusCodes.BAD_GATEWAY || status === StatusCodes.GATEWAY_TIMEOUT;
}

/**
 * Reads which failure the server recorded for an answer that carries a cover error code, or a 500.
 *
 * @param status The HTTP status of the failed call, if it had one.
 * @param title The ProblemDetails title of the failed call, if it had one.
 * @returns The recorded failure, or `undefined` for any other answer.
 */
function recordedFailure(status: number | undefined, title: string | undefined): CoverGenerationFailure | undefined {
  if (status === StatusCodes.INTERNAL_SERVER_ERROR) {
    return CoverGenerationFailure.UNEXPECTED;
  }

  const isCoverError = status === StatusCodes.BAD_GATEWAY || status === StatusCodes.GATEWAY_TIMEOUT;

  return isCoverError && title !== undefined ? FAILURE_BY_COVER_ERROR_CODE.get(title) : undefined;
}
